import React, { useState, useEffect } from "react";
import { Table, Button, Space, Tag, Modal, Typography, Dropdown, Menu, Card, Statistic, Row, Col } from "antd";
import { PlusOutlined, MoreOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { httpGet, httpDelete, customBaseUrl } from "../../store/http";
import NotificationManager from "react-notifications/lib/NotificationManager";
import RewardRuleModal from "./components/RewardRuleModal";
import Layout from '../../components/Layout';

const { Title } = Typography;

const RewardsScreen = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [poolDetails, setPoolDetails] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const fetchPoolBalance = async () => {
    setLoadingBalance(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/reward/pool/balance`);
      if (res?.success || res?.status) {
        setPoolDetails(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch pool balance:", err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchRules = async () => {
    setLoading(true);
    try {
      // Assuming paginated or list response
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/reward/rules`);
      if (res?.success || res?.status) {
        setRules(res.data?.content || res.data || []);
      } else {
        NotificationManager.error(res?.message || "Failed to load reward rules");
      }
    } catch (err) {
      NotificationManager.error("An error occurred while fetching reward rules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchPoolBalance();
  }, []);

  const handleCreate = () => {
    setEditingRule(null);
    setIsModalVisible(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setIsModalVisible(true);
  };

  const handleDeactivate = (id) => {
    Modal.confirm({
      title: "Deactivate Reward Rule",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to deactivate this reward rule? It will no longer grant rewards.",
      okText: "Deactivate",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await httpDelete(`${customBaseUrl.tranv2}/api/v1/admin/reward/rules/${id}`);
          if (res?.success || res?.status) {
            NotificationManager.success("Reward rule deactivated successfully");
            fetchRules();
            fetchPoolBalance();
          } else {
            NotificationManager.error(res?.message || "Failed to deactivate rule");
          }
        } catch (err) {
          NotificationManager.error("An error occurred");
        }
      },
    });
  };

  const menu = (record) => (
    <Menu>
      <Menu.Item key="edit" onClick={() => handleEdit(record)}>
        Edit
      </Menu.Item>
      {record.isActive && (
        <Menu.Item key="deactivate" danger onClick={() => handleDeactivate(record.id)}>
          Deactivate
        </Menu.Item>
      )}
    </Menu>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Trigger",
      dataIndex: "triggerType",
      key: "triggerType",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "Reward Type",
      dataIndex: "rewardType",
      key: "rewardType",
    },
    {
      title: "Value",
      dataIndex: "rewardValue",
      key: "rewardValue",
      render: (val, record) => (
        <span>{record.rewardType === "PERCENTAGE" ? `${val}%` : `₦${val}`}</span>
      ),
    },
    {
      title: "Min Tx Amount",
      dataIndex: "minTransactionAmount",
      key: "minTransactionAmount",
      render: (val) => val ? `₦${Number(val).toLocaleString()}` : <span style={{ color: '#aaa' }}>Any</span>,
    },
    {
      title: "Max Tx Amount",
      dataIndex: "maxTransactionAmount",
      key: "maxTransactionAmount",
      render: (val) => val ? `₦${Number(val).toLocaleString()}` : <span style={{ color: '#aaa' }}>Any</span>,
    },
    {
      title: "Grants/User",
      dataIndex: "maxGrantsPerUser",
      key: "maxGrantsPerUser",
      render: (val) => val || 0,
    },
    {
      title: "Pool Limit",
      dataIndex: "totalPoolLimit",
      key: "totalPoolLimit",
      render: (val) => val ? `₦${Number(val).toLocaleString()}` : <span style={{ color: '#aaa' }}>Unlimited</span>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (val) => (
        <Tag color={val ? "success" : "default"}>
          {val ? "ACTIVE" : "INACTIVE"}
        </Tag>
      ),
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) => (
        <span style={{ fontSize: "12px" }}>
          {new Date(record.startDate).toLocaleDateString()} -{" "}
          {new Date(record.endDate).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown overlay={menu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Layout style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
        <Title level={4} style={{ margin: 0 }}>Reward & Earn Rules</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Rule
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col xs={24} sm={12} md={8}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(24, 144, 255, 0.2)",
              color: "#fff"
            }}
          >
            <Statistic
              title={<span style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "14px" }}>Active Reward Pool Balance</span>}
              value={poolDetails?.balance !== undefined ? poolDetails.balance : (typeof poolDetails === 'number' ? poolDetails : 0)}
              precision={2}
              valueStyle={{ color: "#fff", fontSize: "28px", fontWeight: "bold" }}
              prefix="₦"
              loading={loadingBalance}
            />
            {poolDetails?.glAccountName && (
              <div style={{ marginTop: "12px", borderTop: "1px solid rgba(255, 255, 255, 0.2)", paddingTop: "8px", fontSize: "12px", color: "rgba(255, 255, 255, 0.8)" }}>
                <div><strong>GL Name:</strong> {poolDetails.glAccountName}</div>
                <div><strong>GL Code:</strong> {poolDetails.glAccountCode}</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <div style={{ background: "#fff", padding: "20px", borderRadius: "8px" }}>
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <RewardRuleModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSuccess={() => {
          setIsModalVisible(false);
          fetchRules();
          fetchPoolBalance();
        }}
        initialData={editingRule}
      />
    </Layout>
  );
};

export default RewardsScreen;
